import { type ParsedUrlQuery } from 'querystring';
import { type BasketItem, type Location, type MenuProduct } from '@koala/sdk';
import { type BearerToken } from '@koala/sdk/v4';
import { type AnyAction } from 'redux';
import { type IGlobalFeatureBag, type FEATURE_FLAGS } from '@/constants/features';
import { type IGlobalToastReopen } from '@/types/global';
import { CONNECTION_RETRY_ACTION, CONNECTION_RETRY_TITLE } from '@/utils/errorReporter';

const globalActions = {
  SET_TOKEN: 'koala/global/SET_TOKEN',
  REMOVE_TOKEN: 'koala/global/REMOVE_TOKEN',
  TOGGLE_BASKET: '/koala/global/TOGGLE_BASKET',
  TOGGLE_FULFILLMENT_MODAL: '/koala/global/TOGGLE_FULFILLMENT_MODAL',
  DELIVERY_ADDRESS_VALID: '/koala/global/DELIVERY_ADDRESS_VALID',
  DELIVERY_ADDRESS_INVALID: '/koala/global/DELIVERY_ADDRESS_INVALID',
  DISPLAY_TOAST: 'koala/global/DISPLAY_TOAST',
  DISMISS_TOAST: 'koala/global/DISMISS_TOAST',
  DISMISS_RETRY_TOAST: 'koala/global/DISMISS_RETRY_TOAST',
  RENDER_POST_ITEM_ADD_COMPONENT: 'koala/global/RENDER_POST_ITEM_ADD_COMPONENT',
  SET_CART_MIGRATION: 'koala/global/SET_CART_MIGRATION',
  HYDRATE_PERSISTENT_PARAMETERS: 'koala/global/HYDRATE_PERSISTENT_PARAMETERS',
  SET_PERSISTENT_PARAMETERS: 'koala/global/SET_PERSISTENT_PARAMETERS',
  CLEAR_PERSISTENT_PARAMETERS: 'koala/global/CLEAR_PERSISTENT_PARAMETERS',
  CLEAR_PERSISTENT_PARAMETERS_SUCCESS: 'koala/global/CLEAR_PERSISTENT_PARAMETERS_SUCCESS',
  SET_ROUTER_PATH: 'koala/global/SET_ROUTER_PATH',
  GENERATE_FEATURE_BAG: 'koala/global/GENERATE_FEATURE_BAG',
  UPDATE_FEATURE_BAG: 'koala/global/UPDATE_FEATURE_BAG',
  UPDATE_FEATURE_BAG_FLAG: 'koala/global/UPDATE_FEATURE_BAG_FLAG',
  CLEAR_FEATURE_BAG: 'koala/global/CLEAR_FEATURE_BAG',
  SHOW_REORDER_BANNER: 'koala/global/SHOW_REORDER_BANNER',
  HIDE_REORDER_BANNER: 'koala/global/HIDE_REORDER_BANNER',

  setToken: (token: BearerToken) => ({
    type: globalActions.SET_TOKEN,
    token,
  }),
  removeToken: () => ({
    type: globalActions.REMOVE_TOKEN,
  }),
  toggleBasket: (show: boolean) => ({
    type: globalActions.TOGGLE_BASKET,
    show,
  }),
  toggleFulfillmentModal: (
    show: boolean,
    location?: Location,
    fulfillmentType?: string,
    confirm?: boolean,
    timeslotOverride?: string,
    revalidate?: boolean,
    disableModalClose?: boolean,
    miniConveyanceSwitch?: boolean,
  ) => ({
    type: globalActions.TOGGLE_FULFILLMENT_MODAL,
    show,
    location,
    fulfillmentType,
    confirm,
    timeslotOverride,
    revalidate,
    disableModalClose,
    miniConveyanceSwitch,
  }),
  /**
   * `secondaryMessage` renders as a second line under `message`. It exists so a
   * two-line toast does not have to smuggle markup through `message`, which the
   * component hands to dangerouslySetInnerHTML (TEAM-14387).
   *
   * `reopen` adds a second affordance beside `refresh` (TEAM-15138) — see `IGlobalToastReopen`.
   * Both creators carry it because both surfaces need it: cart revalidation dispatches a success
   * toast, the add-item rejection an error one.
   */
  displayToast: (
    message: string,
    forceDismiss?: boolean,
    refresh?: boolean,
    secondaryMessage?: string,
    reopen?: IGlobalToastReopen,
  ) => ({
    type: globalActions.DISPLAY_TOAST,
    message,
    status: 'success',
    forceDismiss,
    refresh,
    secondaryMessage,
    reopen,
  }),
  displayErrorToast: (
    error: string,
    forceDismiss?: boolean,
    refresh?: boolean,
    reopen?: IGlobalToastReopen,
  ) => ({
    type: globalActions.DISPLAY_TOAST,
    message: error,
    status: 'error',
    forceDismiss,
    refresh,
    reopen,
  }),
  /**
   * The shared "can't connect" copy as a toast, with a "Try again" that re-dispatches `retry`.
   * For surfaces whose failure is already reported through the global toast rather than an
   * inline state — so the wording matches the account pages and auth forms (TEAM-14780).
   */
  displayConnectionErrorToast: (retry: AnyAction) => ({
    type: globalActions.DISPLAY_TOAST,
    message: CONNECTION_RETRY_TITLE,
    secondaryMessage: CONNECTION_RETRY_ACTION,
    status: 'error',
    forceDismiss: true,
    retry,
  }),
  dismissToast: () => ({
    type: globalActions.DISMISS_TOAST,
  }),
  /**
   * Dismisses the toast only if it carries a `retry` (see `displayConnectionErrorToast`). A
   * stored retry re-dispatches the request that failed on the page the diner was on, so it
   * must not outlive that page — pressed later elsewhere it would re-fetch, e.g., the previous
   * store's location over the current one (code review run 5). Every other toast is left
   * alone: some persist across navigation on purpose (TEAM-15138, `types/global.ts`).
   */
  dismissRetryToast: () => ({
    type: globalActions.DISMISS_RETRY_TOAST,
  }),
  setCartMigration: (type?: string, pendingItem?: MenuProduct) => ({
    type: globalActions.SET_CART_MIGRATION,
    cartMigration: {
      type,
      pendingItem,
    },
  }),
  renderPostItemAddComponent: (item?: BasketItem, index?: number) => ({
    type: globalActions.RENDER_POST_ITEM_ADD_COMPONENT,
    item,
    index,
  }),
  hydratePersistentParameters: (routeParameters: ParsedUrlQuery) => ({
    type: globalActions.HYDRATE_PERSISTENT_PARAMETERS,
    routeParameters,
  }),
  setPersistentParameters: (routeParameters: ParsedUrlQuery) => ({
    type: globalActions.SET_PERSISTENT_PARAMETERS,
    routeParameters,
  }),
  clearPersistentParameters: () => ({
    type: globalActions.CLEAR_PERSISTENT_PARAMETERS,
  }),
  setRouterPathname: (pathname: string) => ({
    type: globalActions.SET_ROUTER_PATH,
    pathname,
  }),
  generateFeatureBag: () => ({
    type: globalActions.GENERATE_FEATURE_BAG,
  }),
  updateFeatureBag: (featureBag: IGlobalFeatureBag) => ({
    type: globalActions.UPDATE_FEATURE_BAG,
    featureBag,
  }),
  updateFeatureBagFlag: (featureFlag: FEATURE_FLAGS) => ({
    type: globalActions.UPDATE_FEATURE_BAG_FLAG,
    featureFlag,
  }),
  clearFeatureBag: () => ({
    type: globalActions.CLEAR_FEATURE_BAG,
  }),
  showReorderBanner: () => ({
    type: globalActions.SHOW_REORDER_BANNER,
  }),
  hideReorderBanner: () => ({
    type: globalActions.HIDE_REORDER_BANNER,
  }),
};

export default globalActions;
